// src/components/Voiceassistant/VoiceShoppingAssistant.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './VoiceShoppingAssistant.css';

const VoiceShoppingAssistant = ({ products }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [isOpen, setIsOpen] = useState(true); // ✅ NEW: Toggle state
  const recognitionRef = useRef(null);
  const navigate = useNavigate();

  const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

  // ✅ Get all unique categories from products
  const getAllCategories = () => {
    const categories = [...new Set(products.map(p => p.category || 'General'))];
    return categories.filter(c => c);
  };

  // ✅ Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setIsSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('✅ Voice recognition started');
        setIsListening(true);
      };

      recognition.onresult = async (event) => {
        const speechResult = event.results[0][0].transcript;
        console.log('🎤 Heard:', speechResult);
        setTranscript(speechResult);
        setIsListening(false);
        await processVoiceCommand(speechResult);
      };

      recognition.onerror = (event) => {
        console.error('❌ Speech recognition error:', event.error);
        setIsListening(false);

        let errorMessage = 'Sorry, I couldn\'t hear that. Please try again.';

        if (event.error === 'not-allowed') {
          errorMessage = '🎤 Microphone access denied. Please enable microphone in browser settings.';
        } else if (event.error === 'no-speech') {
          errorMessage = 'No speech detected. Please speak clearly.';
        } else if (event.error === 'network') {
          errorMessage = 'Network error. Please check your connection.';
        }

        setResponse(errorMessage);
        speak(errorMessage);
      };

      recognition.onend = () => {
        console.log('🛑 Voice recognition ended');
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
      console.warn('⚠️ Speech Recognition not supported in this browser');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // ✅ Start Listening
  const startListening = () => {
    if (!isSupported) {
      alert('⚠️ Voice recognition is not supported in your browser.\n\nPlease use Chrome, Edge, or Safari.');
      return;
    }

    if (!recognitionRef.current) {
      alert('⚠️ Voice recognition not initialized. Please refresh the page.');
      return;
    }

    try {
      setIsListening(true);
      setTranscript('');
      setResponse('');
      setMatchedProducts([]);
      console.log('🎤 Starting voice recognition...');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start recognition:', error);
      setIsListening(false);

      if (error.message.includes('already started')) {
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
          } catch (e) {
            alert('Failed to start voice recognition. Please try again.');
          }
        }, 500);
      } else {
        alert('Failed to start voice recognition. Please try again.');
      }
    }
  };

  // ✅ Stop Listening
  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  // ✅ Text-to-Speech
  const speak = (text) => {
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis not supported');
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    utterance.lang = 'en-US';

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  };

  // ✅ Stop Speaking
  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // ✅ NEW: Add to Wishlist Function
  const addToWishlist = (product) => {
    const wishlistItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
      modelSrc: product.modelSrc,
      iOSSrc: product.iOSSrc,
      selectedColor: product.color || 'Default',
      quantity: 1,
      rating: product.rating,
      description: product.description,
      annotations: product.annotations || [],
      addedAt: new Date().toISOString()
    };

    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    const existingIndex = wishlist.findIndex(item => item.id === product.id);

    if (existingIndex > -1) {
      wishlist[existingIndex].quantity += 1;
      const responseMsg = `Updated ${product.name} quantity in wishlist! Now you have ${wishlist[existingIndex].quantity} items.`;
      setResponse(responseMsg);
      speak(responseMsg);
    } else {
      wishlist.push(wishlistItem);
      const responseMsg = `✓ Added ${product.name} to your wishlist!`;
      setResponse(responseMsg);
      speak(responseMsg);
    }

    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    triggerWishlistUpdate(); // ✅ ADDED: Trigger update event
    console.log('✅ Added to wishlist:', product.name);
  };


  // ✅ NEW: Smart Product Name Matcher (Works for all 21 products)
  const findProductByName = (productName) => {
    const searchTerm = productName.toLowerCase().trim();

    // Remove common words
    const cleanedSearch = searchTerm
      .replace(/\b(the|this|that|a|an|to|my|in)\b/g, '')
      .trim();

    console.log('🔍 Searching for product:', cleanedSearch);

    // Strategy 1: Exact name match
    let product = products.find(p =>
      p.name.toLowerCase() === cleanedSearch ||
      p.name.toLowerCase() === searchTerm
    );

    // Strategy 2: Contains full search term
    if (!product) {
      product = products.find(p =>
        p.name.toLowerCase().includes(cleanedSearch) ||
        cleanedSearch.includes(p.name.toLowerCase())
      );
    }

    // Strategy 3: Word-by-word matching (handles partial names)
    if (!product) {
      const searchWords = cleanedSearch.split(' ').filter(w => w.length > 2);

      product = products.find(p => {
        const productWords = p.name.toLowerCase().split(' ');
        const matchCount = searchWords.filter(sw =>
          productWords.some(pw =>
            pw.includes(sw) ||
            sw.includes(pw) ||
            pw.startsWith(sw) ||
            sw.startsWith(pw)
          )
        ).length;

        // Match if at least 50% of words match
        return matchCount >= Math.ceil(searchWords.length / 2);
      });
    }

    // Strategy 4: Category + type matching (e.g., "office chair")
    if (!product) {
      product = products.find(p => {
        const productText = `${p.name} ${p.category || ''} ${p.description || ''}`.toLowerCase();
        return productText.includes(cleanedSearch);
      });
    }

    if (product) {
      console.log('✅ Found product:', product.name);
    } else {
      console.log('❌ No product found for:', searchTerm);
      console.log('Available products:', products.map(p => p.name).slice(0, 10));
    }

    return product;
  };

  // ✅ NEW: Trigger wishlist update event
  const triggerWishlistUpdate = () => {
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('wishlistUpdated'));

    // Also trigger storage event for cross-tab sync
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'wishlist',
      newValue: localStorage.getItem('wishlist'),
      url: window.location.href
    }));

    console.log('🔔 Wishlist update event triggered');
  };

  // ✅ FINAL BULLETPROOF: Process Voice Command
  const processVoiceCommand = async (command) => {
    setIsProcessing(true);
    setMatchedProducts([]);

    try {
      const lowerCommand = command.toLowerCase().trim();
      console.log('🎤 Processing command:', lowerCommand);

      // ====== 1. SHOW ALL CATEGORIES ======
      if ((lowerCommand.includes('all categories') ||
        lowerCommand.includes('all category') ||
        lowerCommand.includes('what categories') ||
        lowerCommand.includes('list categories') ||
        (lowerCommand.includes('show') && lowerCommand.includes('categories'))) &&
        !lowerCommand.includes('wishlist')) {

        console.log('✅ Detected SHOW ALL CATEGORIES command');

        const categories = getAllCategories();
        const categoryList = categories.join(', ');
        const responseText = `We have ${categories.length} categories: ${categoryList}. What would you like to explore?`;

        setResponse(responseText);
        speak(responseText);
        setIsProcessing(false);
        return;
      }

      // ====== 2. WISHLIST COMMANDS (BULLETPROOF) ======
      if (lowerCommand.includes('wishlist') || lowerCommand.includes('wish list')) {

        // ✅ A. ADD TO WISHLIST (Check using regex for precise word matching)
        const hasAddWord = /\b(add|save|put)\b/.test(lowerCommand);

        if (hasAddWord) {
          console.log('✅ Detected ADD TO WISHLIST command');

          // Extract product name
          let productName = lowerCommand
            .replace(/\b(add|save|put|to|in|into|on|my|the|this|that|a|an|wishlist|wish list|cart)\b/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          console.log('🔍 Extracted product name:', productName);

          const product = findProductByName(productName);

          if (product) {
            addToWishlist(product);
          } else {
            const suggestions = products
              .filter(p => {
                const pName = p.name.toLowerCase();
                return productName.split(' ').some(word =>
                  word.length > 2 && pName.includes(word)
                );
              })
              .slice(0, 3)
              .map(p => p.name);

            let responseMsg = `Sorry, I couldn't find "${productName}".`;

            if (suggestions.length > 0) {
              responseMsg += ` Did you mean: ${suggestions.join(', ')}? Please say the exact name.`;
            } else {
              responseMsg += ` Try saying: "Add [exact product name] to wishlist"`;
            }

            setResponse(responseMsg);
            speak(responseMsg);
          }

          setIsProcessing(false);
          return;
        }

        // ✅ B. REMOVE FROM WISHLIST
        const hasRemoveWord = /\b(remove|delete|clear|take out)\b/.test(lowerCommand);

        if (hasRemoveWord) {
          console.log('✅ Detected REMOVE FROM WISHLIST command');

          // Check for "all items" or "everything"
          if (lowerCommand.includes('all item') ||
            lowerCommand.includes('everything') ||
            lowerCommand.includes('all product') ||
            lowerCommand === 'clear wishlist') {

            const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');

            if (wishlist.length > 0) {
              localStorage.setItem('wishlist', JSON.stringify([]));
              triggerWishlistUpdate(); // ✅ ADDED: Trigger update event

              const responseMsg = `✓ Removed all ${wishlist.length} items from your wishlist!`;
              setResponse(responseMsg);
              speak(responseMsg);
            } else {
              const responseMsg = 'Your wishlist is already empty!';
              setResponse(responseMsg);
              speak(responseMsg);
            }

            setIsProcessing(false);
            return;
          }

          // Extract single product name
          let productName = lowerCommand
            .replace(/\b(remove|delete|clear|take out|from|the|this|that|item|my|a|an|wishlist|wish list|cart)\b/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          console.log('🔍 Extracted product name to remove:', productName);

          const product = findProductByName(productName);

          if (product) {
            const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
            const itemExists = wishlist.some(item => item.id === product.id);
            if (itemExists) {
              const updatedWishlist = wishlist.filter(item => item.id !== product.id);
              localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
              triggerWishlistUpdate(); // ✅ ADDED: Trigger update event

              const responseMsg = `✓ Removed ${product.name} from your wishlist!`;
              setResponse(responseMsg);
              speak(responseMsg);
            } else {
              const responseMsg = `${product.name} is not in your wishlist.`;
              setResponse(responseMsg);
              speak(responseMsg);
            }
          } else {
            const responseMsg = `Sorry, I couldn't find "${productName}" to remove. Please say the exact product name.`;
            setResponse(responseMsg);
            speak(responseMsg);
          }

          setIsProcessing(false);
          return;
        }

        // ✅ C. LIST WISHLIST ITEMS (What's in wishlist, which items, etc.)
        if (lowerCommand.includes('which item') ||
          lowerCommand.includes('what item') ||
          lowerCommand.includes('name the item') ||
          lowerCommand.includes('list item') ||
          lowerCommand.includes('tell me what')) {

          console.log('✅ Detected LIST WISHLIST ITEMS command');

          const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');

          if (wishlist.length > 0) {
            const itemNames = wishlist.map(item => item.name).join(', ');
            const responseText = `You have ${wishlist.length} item${wishlist.length > 1 ? 's' : ''} in your wishlist: ${itemNames}`;

            setResponse(responseText);
            speak(responseText);
          } else {
            const responseText = 'Your wishlist is empty!';
            setResponse(responseText);
            speak(responseText);
          }

          setIsProcessing(false);
          return;
        }

        // ✅ D. VIEW WISHLIST (Default)
        console.log('✅ Detected VIEW WISHLIST command');

        const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
        const responseText = wishlist.length > 0
          ? `Opening your wishlist with ${wishlist.length} items!`
          : 'Your wishlist is empty. Start adding products!';

        setResponse(responseText);
        speak(responseText);

        setTimeout(() => {
          navigate('/wishlist');
        }, 1500);

        setIsProcessing(false);
        return;
      }

      // ====== 3. SHOW PRODUCT DETAILS / OPEN PRODUCT ======
      const detailsKeywords = [
        'product details of', 'details of', 'tell me about', 'open product',
        'view product', 'show product', 'information about',
        'show me details of', 'details about', 'tell me more about'
      ];

      if (detailsKeywords.some(keyword => lowerCommand.includes(keyword))) {
        console.log('✅ Detected PRODUCT DETAILS command');

        let productName = lowerCommand;

        detailsKeywords.forEach(keyword => {
          productName = productName.replace(keyword, '');
        });

        productName = productName
          .replace(/\b(the|this|that|of|about|for|a|an)\b/g, '')
          .trim();

        console.log('🔍 Looking for product details:', productName);

        const product = findProductByName(productName);

        if (product) {
          const responseMsg = `Opening ${product.name} details now! Price: $${product.price}`;
          setResponse(responseMsg);
          speak(responseMsg);

          setTimeout(() => {
            navigate(`/product/${product.id}`);
          }, 1500);
        } else {
          const responseMsg = `Sorry, I couldn't find "${productName}". Please say the exact product name clearly.`;
          setResponse(responseMsg);
          speak(responseMsg);
        }

        setIsProcessing(false);
        return;
      }

      // ====== 4. SEARCH/SHOW PRODUCTS ======
      const searchKeywords = [
        'show me', 'find', 'search', 'get', 'need', 'want',
        'looking for', 'i need', 'i want', 'buy', 'purchase', 'browse',
        'show'
      ];

      const isProductSearch = searchKeywords.some(keyword => lowerCommand.includes(keyword)) ||
        lowerCommand.split(' ').length <= 3; // Changed from 2 to 3

      if (isProductSearch) {
        const productTypes = {
          'chair': ['chair', 'chairs', 'seating', 'seat', 'office chair'],
          'sofa': ['sofa', 'couch', 'sofas', 'couches'],
          'table': ['table', 'desk', 'tables', 'desks'],
          'bed': ['bed', 'bedroom', 'beds'],
          'almirah': ['almirah', 'wardrobe', 'cupboard', 'closet', 'cabinet'],
          'laptop': ['laptop', 'computer', 'laptops', 'macbook', 'notebook'],
          'washing machine': ['washing', 'machine', 'washer', 'laundry'],
          'electronics': ['electronics', 'electronic', 'gadget', 'device', 'appliance'],
          'car': ['car', 'vehicle', 'cars', 'auto', 'automobile'],
          'painting': ['painting', 'paintings', 'paint', 'wall art', 'canvas'],
          'monument': ['monument', 'monuments', 'sculpture', 'statue', 'memorial'],
          'art': ['art', 'artwork', 'artistic', 'fine art'],
          'pot': ['pot', 'planter', 'vase', 'plant pot'],
          'lamp': ['lamp', 'light', 'lighting', 'lights'],
          'decor': ['decor', 'decoration', 'decorative', 'decorations'],
          'furniture': ['furniture', 'furnishing', 'furnishings'],
          'office': ['office', 'workspace', 'work'],
          'home': ['home', 'house', 'living'],
          'premium': ['premium', 'luxury', 'high-end'],
          'modern': ['modern', 'contemporary'],
          'executive': ['executive', 'professional']
        };

        let matchedProducts = [];
        let matchedCategory = '';

        // Strategy 1: Check product types
        for (const [type, keywords] of Object.entries(productTypes)) {
          if (keywords.some(keyword => lowerCommand.includes(keyword))) {
            matchedCategory = type;
            matchedProducts = products.filter(p => {
              const productText = `${p.name} ${p.category || ''} ${p.description || ''}`.toLowerCase();
              return keywords.some(keyword => productText.includes(keyword));
            });

            console.log(`✅ Matched ${type}:`, matchedProducts.length, 'products');

            if (matchedProducts.length > 0) break;
          }
        }

        // Strategy 2: Exact category match
        if (matchedProducts.length === 0) {
          const categories = getAllCategories();
          for (const category of categories) {
            if (lowerCommand.includes(category.toLowerCase())) {
              matchedProducts = products.filter(p =>
                (p.category || 'General').toLowerCase() === category.toLowerCase()
              );
              matchedCategory = category;
              console.log(`✅ Matched category ${category}:`, matchedProducts.length, 'products');
              break;
            }
          }
        }

        // Strategy 3: Try to find exact product by name
        if (matchedProducts.length === 0 && lowerCommand.split(' ').length <= 4) {
          const product = findProductByName(lowerCommand);
          if (product) {
            // Single product found - show details
            const responseMsg = `Found ${product.name} for $${product.price}! Opening details...`;
            setResponse(responseMsg);
            speak(responseMsg);

            setTimeout(() => {
              navigate(`/product/${product.id}`);
            }, 1500);

            setIsProcessing(false);
            return;
          }
        }

        // Strategy 4: Broad search
        if (matchedProducts.length === 0) {
          const searchTerms = lowerCommand
            .replace(/show|me|find|get|need|want|looking|for|i|the|a|an/g, '')
            .trim()
            .split(' ')
            .filter(term => term.length > 2);

          matchedProducts = products.filter(p => {
            const productText = `${p.name} ${p.category || ''} ${p.description || ''}`.toLowerCase();
            return searchTerms.some(term => productText.includes(term));
          });

          matchedCategory = searchTerms.join(' ');
          console.log(`✅ Broad search matched:`, matchedProducts.length, 'products');
        }

        // Display results
        if (matchedProducts.length > 0) {
          setMatchedProducts(matchedProducts);

          const responseText = `Found ${matchedProducts.length} ${matchedCategory}${matchedProducts.length > 1 ? 's' : ''} for you! Showing all results below.`;

          setResponse(responseText);
          speak(responseText);

          sessionStorage.setItem('aiSearchResults', JSON.stringify(matchedProducts));
          setTimeout(() => {
            navigate('/');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 2000);

          setIsProcessing(false);
          return;
        }
      }

      // ====== 5. FALLBACK ======
      const categories = getAllCategories();
      const sampleProducts = products.slice(0, 3).map(p => p.name).join(', ');

      const fallback = `I can help you with: showing products, adding to wishlist, viewing product details, or opening your wishlist. Try: "Show me ${categories[0]}", "Add ${products[0]?.name || 'a product'} to wishlist", "Show my wishlist", or "Product details of ${products[1]?.name || 'any product'}"`;

      setResponse(fallback);
      speak(fallback);

    } catch (error) {
      console.error('❌ Voice processing error:', error);
      const categories = getAllCategories();
      const fallback = `Something went wrong. Try: "Show me ${categories[0]}" or "Show my wishlist"`;
      setResponse(fallback);
      speak(fallback);
    } finally {
      setIsProcessing(false);
    }
  };


  // ✅ Don't render if not supported
  if (!isSupported) {
    return (
      <div className="voice-assistant-unsupported">
        <p>⚠️ Voice recognition not supported in this browser.</p>
        <p>Please use Chrome, Edge, or Safari.</p>
      </div>
    );
  }

  // ✅ NEW: If closed, show mini button
  // ✅ UPDATED: Better icons for voice assistant

  // When closed - Mini button
  if (!isOpen) {
    return (
      <button
        className="voice-mini-button"
        onClick={() => setIsOpen(true)}
        aria-label="Open Voice Shopping"
      >
        🎙️
      </button>
    );
  }

  return (
    <div className="voice-shopping-assistant">
      <div className="voice-assistant-panel">
        {/* ✅ FIXED: Header stays fixed at top */}
        <div className="voice-header">
          <h3>🎙️ Voice Shop</h3>
          <button
            className="voice-close-btn"
            onClick={() => {
              stopSpeaking();
              stopListening();
              setIsOpen(false);
            }}
            aria-label="Close Voice Shop"
          >
            ✕
          </button>
        </div>

        {/* ✅ FIXED: Only this area scrolls */}
        <div className="voice-content-area">
          <p className="voice-hint">
            Try: "Show me chairs", "All categories", or just say "painting"
          </p>

          <div className="voice-controls">
            <button
              className={`voice-btn ${isListening ? 'listening' : ''} ${isProcessing ? 'processing' : ''}`}
              onClick={isListening ? stopListening : startListening}
              disabled={isProcessing}
            >
              {isListening ? (
                <>🎙️ Listening...</>
              ) : isProcessing ? (
                <>⏳ Processing...</>
              ) : (
                <>🎙️ Start Voice Shop</>
              )}
            </button>

            {isSpeaking && (
              <button
                className="stop-speaking-btn"
                onClick={stopSpeaking}
              >
                🔇 Stop Speaking
              </button>
            )}
          </div>

          {(transcript || response) && (
            <div className="voice-feedback">
              {transcript && (
                <div className="voice-transcript">
                  <strong>You said:</strong> "{transcript}"
                </div>
              )}

              {response && (
                <div className="voice-response">
                  <strong>🤖 Assistant:</strong> {response}
                </div>
              )}

              {matchedProducts.length > 0 && (
                <div className="voice-products-preview">
                  <p><strong>Found {matchedProducts.length} products:</strong></p>
                  <ul>
                    {matchedProducts.slice(0, 5).map(p => (
                      <li key={p.id}>{p.name} - ${p.price}</li>
                    ))}
                    {matchedProducts.length > 5 && (
                      <li>...and {matchedProducts.length - 5} more!</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
};

export default VoiceShoppingAssistant;
